import React, { useEffect, useState, useCallback } from "react";
import Wrapper from "../../../components/TenantWrapper";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Fade,
  Skeleton,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { GetTenantInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
import { CreateSafePaySession } from "../../../redux/reducer/SafePayPayment";
import { GetPublicSettings } from "../../../redux/reducer/SystemSettings";
import { useSearchParams } from "react-router-dom";
import {
  calculateAdvanceAndRentCommission,
  calculateAdvancePayment,
  updateCommissionRates,
} from "../../../utils/commissionCalculator";
import { BASE_URL, PAYMENT_BASE_URL } from "../../../config/config";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import title from "../../../components/title";

// Import sub-components
import RequestCard from "./RequestCard";
import StatisticsCards from "./StatisticsCards";
import SearchBar from "./SearchBar";
import TabsSection from "./TabsSection";
import EmptyState from "./EmptyState";
import PaymentDialog from "./PaymentDialog";

export default function MyRequests() {
  title("My Requests");

  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { settings: commissionSettings, loading: settingsLoading } =
    useSelector((state) => state.systemSettings);
  const { validateUser } = useSelector((state) => state);
  const [searchParams, setSearchParams] = useSearchParams();
  const flatIdFromUrl = searchParams.get("flatId");
  const initialTab = parseInt(searchParams.get("tab")) || 0;
  const initialSearch = searchParams.get("search") || "";
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [requests, setRequests] = useState([]);
  /* `requests` gets filtered down to a single flat when the page is opened
     from a payment link, and the "one booking at a time" check has to see
     every request or the link becomes a way around it. */
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    request: null,
  });
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  // Cash with an OTS service fee: the tenant also uploads the receipt for the
  // fee they paid to OTS's own account.
  const [otsFeeReceiptImage, setOtsFeeReceiptImage] = useState(null);
  const [otsFeeReceiptPreview, setOtsFeeReceiptPreview] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  const tenantId =
    localStorage.getItem("user_id") || localStorage.getItem("userId");

  // Update URL params when search query or tab changes
  useEffect(() => {
    if (flatIdFromUrl) return;

    const params = {};
    if (activeTab !== 0) {
      params.tab = activeTab.toString();
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    setSearchParams(params, { replace: true });
  }, [searchQuery, activeTab, flatIdFromUrl, setSearchParams]);

  // Fetch commission settings on mount
  useEffect(() => {
    dispatch(GetPublicSettings()).then(() => {
      setSettingsLoaded(true);
    });
  }, [dispatch]);

  // Update commission rates when settings change
  useEffect(() => {
    if (commissionSettings) {
      updateCommissionRates(commissionSettings);
    }
  }, [commissionSettings]);

  const customRates = commissionSettings
    ? {
        ADVANCE: (commissionSettings.advanceCommission ?? 0) / 100,
        MONTHLY_RENT: (commissionSettings.rentCommission ?? 0) / 100,
        LATE_FEE: commissionSettings.lateFee ?? 0,
        GRACE_PERIOD: commissionSettings.gracePeriod ?? 5,
        IS_ACTIVE: commissionSettings.isServiceActive ?? true,
      }
    : null;
  console.log(customRates, "customRates");

  const fetchRequests = useCallback(
    // `silent` is for the window-focus auto-refresh below: it still pulls
    // fresh data, but without flipping loading/tabLoading — those replace
    // the ENTIRE grid with skeletons (see the `loading || tabLoading` check
    // in the render below), so every alt-tab back to this page was wiping
    // the whole list and redrawing it from scratch. Data staying fresh in
    // the background is the point; a full-page flash every time isn't.
    async (showTabLoader = false, silent = false) => {
      if (!tenantId) return;

      if (!silent) {
        if (showTabLoader) {
          setTabLoading(true);
        } else {
          setLoading(true);
        }
      }

      try {
        const res = await dispatch(GetTenantInterestRequests({ tenantId }));
        const allData = res?.payload?.data?.data || [];

        setAllRequests(allData);

        if (flatIdFromUrl) {
          const filtered = allData.filter(
            (r) => r.flatId === flatIdFromUrl || r.flat_id === flatIdFromUrl
          );
          setRequests(filtered);
        } else {
          setRequests(allData);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
      } finally {
        if (!silent) {
          setLoading(false);
          setTabLoading(false);
        }
      }
    },
    [tenantId, flatIdFromUrl]
  );

  // Fetch on mount only - do not include fetchRequests in deps to prevent unnecessary refetches
  useEffect(() => {
    if (tenantId) {
      fetchRequests();
    }
    // Only run on mount when tenantId is available
  }, [tenantId, flatIdFromUrl]);

  // Auto-refresh when window regains focus - use ref to avoid re-attaching listener
  useEffect(() => {
    const handleFocus = () => {
      // Call fetchRequests directly without dependency to prevent re-running this effect
      if (tenantId) {
        fetchRequests(false, true);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // Only set up listener once on mount
  }, []);

  const handleTabChange = async (event, newValue) => {
    setTabLoading(true);
    setActiveTab(newValue);

    await fetchRequests(true);

    setTimeout(() => {
      setTabLoading(false);
    }, 300);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleOpenPaymentDialog = (request) => {
    /* Second line of defence behind the card's own check: a booking already in
       progress means no other flat can be paid for. */
    const bookedId = activeBooking?._id || activeBooking?.id;
    if (bookedId && bookedId !== (request?._id || request?.id)) return;

    setPaymentDialog({ open: true, request });
    setPaymentMethod("cash");
    setReceiptImage(null);
    setReceiptPreview(null);
    setOtsFeeReceiptImage(null);
    setOtsFeeReceiptPreview(null);
    setPaymentError("");
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog({ open: false, request: null });
    setReceiptImage(null);
    setReceiptPreview(null);
    setOtsFeeReceiptImage(null);
    setOtsFeeReceiptPreview(null);
    setPaymentError("");
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptImage(file);
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handleOtsFeeReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOtsFeeReceiptImage(file);
      setOtsFeeReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handlePayWithSafePay = async () => {
    const { request } = paymentDialog;
    if (!request) return;

    setPaymentLoading(true);
    setPaymentError("");

    try {
      const advanceAmount = Number(request.flatAdvance || 0);
      const rentAmount = Number(request.flatRent || 0);
      const advanceBreakdown = calculateAdvanceAndRentCommission(
        advanceAmount,
        rentAmount,
        customRates
      );

      let totalAmount = advanceBreakdown.totalTenantPays;
      let safePayFee = Math.ceil(totalAmount / 0.9672) - totalAmount;
      totalAmount += safePayFee;

      const pendingPaymentInfo = {
        requestId: request._id || request.id,
        flatId: request.flatId,
        flatName: request.flatName,
        baseAdvance: advanceAmount,
        advanceCommission: advanceBreakdown.commissionAmount,
        firstMonthRent: rentAmount,
        totalAmount: totalAmount,
        billType: "advance",
        tenantId: tenantId,
        landlordId: request.landlordId,
        paymentMethod: "safepay",
      };
      localStorage.setItem(
        "pending_payment",
        JSON.stringify(pendingPaymentInfo)
      );

      const result = await dispatch(
        CreateSafePaySession({
          paymentData: {
            amount: totalAmount,
            baseAdvance: advanceAmount,
            commissionAmount: advanceBreakdown.commissionAmount,
            firstMonthRent: rentAmount,
            billType: "advance",
            billId: request._id || request.id,
            tenantId: tenantId,
            landlordId: request.landlordId,
            flatId: request.flatId,
            description: `Advance payment for ${request.flatName}`,
            processingAmount: safePayFee,
          },
        })
      );

      const checkoutUrl =
        result?.payload?.data?.checkoutUrl ||
        result?.payload?.data?.data?.checkoutUrl ||
        result?.payload?.data?.url;

      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank");
        handleClosePaymentDialog();
      } else {
        localStorage.removeItem("pending_payment");
        setPaymentError(
          result?.payload?.data?.message ||
            "Failed to initiate payment. Please try again."
        );
      }
    } catch (err) {
      localStorage.removeItem("pending_payment");
      setPaymentError("Something went wrong. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };
  const handlePayWithAlfa = async () => {
    const { request } = paymentDialog;

    if (!request) return;

    setPaymentLoading(true);

    setPaymentError("");

    try {
      /*
      CALCULATE AMOUNT
    */

      const advanceAmount = Number(request.flatAdvance || 0);

      const rentAmount = Number(request.flatRent || 0);

      const advanceBreakdown = calculateAdvanceAndRentCommission(
        advanceAmount,
        rentAmount,
        customRates
      );

      let totalAmount = advanceBreakdown.totalTenantPays;
      console.log("Initial Total Amount:", totalAmount);
      /*
      OPTIONAL PROCESSING FEE
    */

      // let alfaFee = 0;
      let alfaFee = totalAmount * 0.025;

      totalAmount += alfaFee;

      /*
      SAVE PENDING PAYMENT
    */

      const pendingPaymentInfo = {
        requestId: request._id || request.id,

        flatId: request.flatId,

        flatName: request.flatName,

        baseAdvance: advanceAmount,

        advanceCommission: advanceBreakdown.commissionAmount,
        processingAmount: alfaFee,
        firstMonthRent: rentAmount,

        totalAmount,

        billType: "advance",

        tenantId,

        landlordId: request.landlordId,

        paymentMethod: "alfa",
      };

      localStorage.setItem(
        "pending_payment",
        JSON.stringify(pendingPaymentInfo)
      );

      /*
      API CALL
    */
      const response = await axios.post(
        `${PAYMENT_BASE_URL}/api/payment/create-payment`,
        {
          amount: totalAmount,

          baseAdvance: advanceAmount,
          baseAmount: advanceAmount + rentAmount,

          commissionAmount: advanceBreakdown.commissionAmount,
          processingAmount: alfaFee,

          firstMonthRent: rentAmount,

          billType: "advance",

          billId: request._id || request.id,

          tenantId,

          landlordId: request.landlordId,

          flatId: request.flatId,

          description: `Advance payment for ${request.flatName}`,
        },
        {
          // Payment endpoints ab token maangte hain — pehle ye poora raasta
          // bina login ke khula tha.
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("token") ||
              sessionStorage.getItem("ots_token")
            }`,
          },
        }
      );

      /*
      RESPONSE
    */
      console.log(response, "response");
      const paymentUrl = response?.data?.paymentUrl;

      const paymentPayload = response?.data?.paymentPayload;

      //   /*
      //   VALIDATION
      // */

      if (!paymentUrl || !paymentPayload) {
        localStorage.removeItem("pending_payment");

        setPaymentError("Failed to initialize payment");

        return;
      }

      //   /*
      //   CREATE FORM
      // */

      const form = document.createElement("form");

      form.method = "POST";

      form.action = paymentUrl;

      //   /*
      //   ADD HIDDEN INPUTS
      // */

      Object.keys(paymentPayload).forEach((key) => {
        const input = document.createElement("input");

        input.type = "hidden";

        input.name = key;

        input.value = paymentPayload[key];

        form.appendChild(input);
      });

      document.body.appendChild(form);

      //   /*
      //   REDIRECT TO ALFA
      // */

      form.submit();

      // handleClosePaymentDialog();
      // Keep loader running during page redirect - don't call setPaymentLoading(false)
    } catch (err) {
      console.log(err);

      localStorage.removeItem("pending_payment");

      setPaymentError(err?.response?.data?.message || "Something went wrong");
      setPaymentLoading(false);
    }
  };

  const handleSubmitReceipt = async () => {
    const { request } = paymentDialog;
    if (!request || !receiptImage) {
      setPaymentError("Please upload a receipt image");
      return;
    }

    // The fee is worked out on the server too; this only decides whether to
    // insist on the second receipt before sending anything.
    const feeBreakdown = calculateAdvanceAndRentCommission(
      request.flatAdvance,
      request.flatRent,
      customRates
    );
    if (feeBreakdown.totalCommission > 0 && !otsFeeReceiptImage) {
      setPaymentError("Please also upload the receipt of your OTS service fee payment");
      return;
    }

    setPaymentLoading(true);
    setPaymentError("");

    try {
      const formData = new FormData();
      formData.append("image", receiptImage);
      if (feeBreakdown.totalCommission > 0 && otsFeeReceiptImage) {
        formData.append("otsFeeImage", otsFeeReceiptImage);
      }
      formData.append("requestId", request._id || request.id);
      formData.append(
        "amount",
        calculateAdvanceAndRentCommission(
          request.flatAdvance,
          request.flatRent,
          customRates
        ).totalTenantPays
      );
      formData.append("flatId", request.flatId);
      formData.append("flatName", request.flatName);
      formData.append("baseAdvance", request.flatAdvance || 0);
      formData.append(
        "commissionAmount",
        calculateAdvanceAndRentCommission(
          request.flatAdvance,
          request.flatRent,
          customRates
        ).totalCommission
      );
      formData.append("firstMonthRent", request.flatRent);
      formData.append("billType", "advance");
      formData.append("billId", request._id || request.id);
      formData.append("tenantId", request.tenantId);
      formData.append("landlordId", request.landlordId);
      formData.append("description", `Advance payment for ${request.flatName}`);
      // return
      const uploadRes = await axios.post(
        `${BASE_URL}/flat-interest/upload-receipt`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadRes?.data?.success) {
        handleClosePaymentDialog();
        fetchRequests();
        toast.success(
          uploadRes.data.otsFee?.required
            ? "Receipts sent. The owner will verify your payment and OTS will verify your service fee."
            : "Receipt sent. The owner will verify your payment."
        );
      } else {
        setPaymentError("Failed to upload receipt. Please try again.");
      }
    } catch (err) {
      // The server names the exact problem (wrong amount, missing OTS fee
      // receipt...) — say it instead of a generic message.
      setPaymentError(
        err?.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  // Filter requests based on search and tab
  const filterRequests = (requests) => {
    if (!searchQuery.trim()) return requests;

    const query = searchQuery.toLowerCase();
    return requests.filter(
      (request) =>
        request.flatName?.toLowerCase().includes(query) ||
        request.landlordName?.toLowerCase().includes(query) ||
        request.moveInDate?.toLowerCase().includes(query) ||
        request.status?.toLowerCase().includes(query)
    );
  };

  const getFilteredRequests = () => {
    let filtered = requests;

    if (flatIdFromUrl) {
      filtered = requests;
    } else {
      switch (activeTab) {
        case 0:
          filtered = requests;
          break;
        case 1:
          filtered = requests.filter((r) => r.status === "pending");
          break;
        case 2:
          filtered = requests.filter(
            (r) => r.status === "accepted" && r.paymentStatus !== "verifying"
          );
          break;
        case 3:
          filtered = requests.filter(
            (r) => r.status === "accepted" && r.paymentStatus === "verifying"
          );
          break;
        case 4:
          filtered = requests.filter((r) => r.status === "rejected");
          break;
        default:
          filtered = requests;
      }
    }

    return filterRequests(filtered);
  };

  const filteredRequests = getFilteredRequests();

  // Calculate statistics
  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    accepted: requests.filter(
      (r) => r.status === "accepted" && r.paymentStatus !== "verifying"
    ).length,
    verifying: requests.filter(
      (r) => r.status === "accepted" && r.paymentStatus === "verifying"
    ).length,
    rejected: requests.filter((r) => r.status === "rejected").length,
  };

  /* The request the tenant has already put money behind — paid and waiting on
     the owner, verified, or moved in. A tenant can only be booking one flat at
     a time, so while this exists every other request's payment is closed off.
     Cancelling the booking (`moveInStatus: "cancelled"`) releases the hold and
     the other flats become payable again.

     This used to only look for "verifying", which meant the moment an owner
     verified the payment the other flats' Make Payment buttons came back and a
     tenant with a confirmed booking could pay a second owner by mistake. */
  const activeBooking = allRequests.find(
    (val) =>
      (val.paymentStatus === "verifying" || val.paymentStatus === "verified") &&
      val.moveInStatus !== "cancelled"
  );

  // Skeleton Card Component
  const SkeletonCard = () => (
    <Grid item xs={12} md={6} lg={4}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Skeleton width="60%" height={32} />
            <Skeleton width={80} height={32} />
          </Box>
          <Skeleton width="100%" height={40} sx={{ mb: 2 }} />
          <Stack spacing={1}>
            <Skeleton width="90%" height={20} />
            <Skeleton width="85%" height={20} />
            <Skeleton width="80%" height={20} />
            <Skeleton width="75%" height={20} />
          </Stack>
          <Skeleton width="100%" height={48} sx={{ mt: 2, borderRadius: 2 }} />
        </CardContent>
      </Card>
    </Grid>
  );

  // Get no data message
  const getNoDataMessage = () => {
    if (flatIdFromUrl) {
      return {
        title: "No Payment Request Found",
        description: "The payment link may have expired or is invalid.",
      };
    }

    const messages = {
      0: {
        title: "No Requests Yet",
        description: searchQuery
          ? `No requests match your search "${searchQuery}"`
          : "You haven't sent any property interest requests yet. Start exploring properties!",
      },
      1: {
        title: "No Pending Requests",
        description: searchQuery
          ? `No pending requests match your search "${searchQuery}"`
          : "You don't have any pending requests awaiting landlord approval.",
      },
      2: {
        title: "No Accepted Requests",
        description: searchQuery
          ? `No accepted requests match your search "${searchQuery}"`
          : "No requests have been accepted yet. Once a landlord accepts, they'll appear here.",
      },
      3: {
        title: "No Payments Being Verified",
        description: searchQuery
          ? `No verifying payments match your search "${searchQuery}"`
          : "You don't have any payments currently being verified by landlords.",
      },
      4: {
        title: "No Rejected Requests",
        description: searchQuery
          ? `No rejected requests match your search "${searchQuery}"`
          : "Great! You don't have any rejected requests.",
      },
    };

    return messages[activeTab] || messages[0];
  };

  const noDataInfo = getNoDataMessage();

  return (
    <>
      <Wrapper />

      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header Section */}
          <Fade in={true} timeout={800}>
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {flatIdFromUrl ? "Payment Request" : "My Requests"}
                </Typography>
                {flatIdFromUrl && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => (window.location.href = "/my-requests")}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    View All Requests
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {flatIdFromUrl
                  ? "Complete your payment to confirm booking."
                  : "Track your property interest requests and make payments."}
              </Typography>
            </Box>
          </Fade>

          {/* Statistics Cards */}
          {!flatIdFromUrl && (
            <StatisticsCards stats={stats} loading={loading} />
          )}

          {/* Search Bar */}
          {!flatIdFromUrl && (
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              loading={loading}
              tabLoading={tabLoading}
              resultsCount={filteredRequests.length}
            />
          )}

          {/* Tabs Section */}
          {!flatIdFromUrl && (
            <TabsSection
              activeTab={activeTab}
              onTabChange={handleTabChange}
              stats={stats}
              loading={loading}
              tabLoading={tabLoading}
            />
          )}

          {/* Requests Grid */}
          <Box>
            {loading || tabLoading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <SkeletonCard key={item} />
                ))}
              </Grid>
            ) : filteredRequests.length === 0 ? (
              <EmptyState
                title={noDataInfo.title}
                description={noDataInfo.description}
                searchQuery={searchQuery}
                onClearSearch={handleClearSearch}
                flatIdFromUrl={flatIdFromUrl}
              />
            ) : (
              <Fade in={true} timeout={600}>
                <Grid container spacing={3}>
                  {filteredRequests.map((request) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={4}
                      key={request._id || request.id}
                    >
                      <RequestCard
                        request={request}
                        customRates={customRates}
                        onOpenPaymentDialog={handleOpenPaymentDialog}
                        settingsLoading={settingsLoading}
                        settingsLoaded={settingsLoaded}
                        activeBooking={activeBooking}
                        onRefresh={fetchRequests}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Fade>
            )}
          </Box>
        </Container>

        {/* Payment Dialog */}
        <PaymentDialog
          open={paymentDialog.open}
          request={paymentDialog.request}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={(e) => setPaymentMethod(e.target.value)}
          receiptImage={receiptImage}
          receiptPreview={receiptPreview}
          onReceiptUpload={handleReceiptUpload}
          otsFeeReceiptImage={otsFeeReceiptImage}
          otsFeeReceiptPreview={otsFeeReceiptPreview}
          onOtsFeeReceiptUpload={handleOtsFeeReceiptUpload}
          otsAccount={{
            bankName: commissionSettings?.otsBankName,
            accountTitle: commissionSettings?.otsAccountTitle,
            accountNumber: commissionSettings?.otsAccountNumber,
            iban: commissionSettings?.otsIban,
          }}
          paymentError={paymentError}
          paymentLoading={paymentLoading}
          settingsLoaded={settingsLoaded}
          settingsLoading={settingsLoading}
          customRates={customRates}
          onClose={handleClosePaymentDialog}
          onPayWithSafePay={handlePayWithSafePay}
          onPayWithAlfa={handlePayWithAlfa}
          onSubmitReceipt={handleSubmitReceipt}
        />

        {/* Page-level, not dialog-level. This used to live inside
            PaymentDialog, which returns null whenever no request is selected —
            so every toast fired from anywhere else on this page, the move-in
            panel included, had no container to land in and vanished. */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="colored"
        />
      </div>
    </>
  );
}
// import React, { useEffect, useState, useCallback } from "react";
// import Wrapper from "../../../components/TenantWrapper";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Chip,
//   Grid,
//   Divider,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   CircularProgress,
//   Alert,
//   Paper,
//   Radio,
//   RadioGroup,
//   FormControlLabel,
//   FormControl,
//   FormLabel,
//   Stepper,
//   Step,
//   StepLabel,
//   Tabs,
//   Tab,
//   Container,
//   TextField,
//   InputAdornment,
//   Badge,
//   Fade,
//   Zoom,
//   Avatar,
//   LinearProgress,
//   Skeleton,
//   Stack,
// } from "@mui/material";
// import { useSelector, useDispatch } from "react-redux";
// import { GetTenantInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
// import { CreateSafePaySession } from "../../../redux/reducer/SafePayPayment";
// import { GetPublicSettings } from "../../../redux/reducer/SystemSettings";
// import moment from "moment";
// import HomeIcon from "@mui/icons-material/Home";
// import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import CancelIcon from "@mui/icons-material/Cancel";
// import PaymentIcon from "@mui/icons-material/Payment";
// import ReceiptIcon from "@mui/icons-material/Receipt";
// import CloudUploadIcon from "@mui/icons-material/CloudUpload";
// import CreditCardIcon from "@mui/icons-material/CreditCard";
// import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
// import LockIcon from "@mui/icons-material/Lock";
// import SearchIcon from "@mui/icons-material/Search";
// import ClearIcon from "@mui/icons-material/Clear";
// import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// import PersonIcon from "@mui/icons-material/Person";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
// import AssignmentIcon from "@mui/icons-material/Assignment";
// import title from "../../../components/title";
// import { BASE_URL } from "../../../config/config";
// import axios from "axios";
// import { useSearchParams } from "react-router-dom";
// import PaymentBreakdown from "../../../components/PaymentBreakdown";
// import {
//   calculateAdvancePayment,
//   formatCurrency,
//   updateCommissionRates,
// } from "../../../utils/commissionCalculator";
// import Slider from "react-slick";
// import cardPic from "../../../assets/card_pic.webp";

// export default function MyRequests() {
//   title("My Requests");

//   const dispatch = useDispatch();
//   const open = useSelector((state) => state.sidebar.open);
//   const { settings: commissionSettings, loading: settingsLoading } =
//     useSelector((state) => state.systemSettings);
//   const [searchParams, setSearchParams] = useSearchParams();
//   const flatIdFromUrl = searchParams.get("flatId");
//   const initialTab = parseInt(searchParams.get("tab")) || 0;
//   const initialSearch = searchParams.get("search") || "";
//   const [settingsLoaded, setSettingsLoaded] = useState(false);

//   const [requests, setRequests] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [tabLoading, setTabLoading] = useState(false);
//   const [paymentDialog, setPaymentDialog] = useState({
//     open: false,
//     request: null,
//   });
//   const [paymentMethod, setPaymentMethod] = useState("safepay");
//   const [receiptImage, setReceiptImage] = useState(null);
//   const [receiptPreview, setReceiptPreview] = useState(null);
//   const [paymentLoading, setPaymentLoading] = useState(false);
//   const [paymentError, setPaymentError] = useState("");
//   const [activeTab, setActiveTab] = useState(initialTab);
//   const [searchQuery, setSearchQuery] = useState(initialSearch);

//   const tenantId =
//     localStorage.getItem("user_id") || localStorage.getItem("userId");

//   // Update URL params when search query or tab changes
//   useEffect(() => {
//     if (flatIdFromUrl) return; // Don't update params when flatId exists

//     const params = {};
//     if (activeTab !== 0) {
//       params.tab = activeTab.toString();
//     }
//     if (searchQuery.trim()) {
//       params.search = searchQuery.trim();
//     }
//     setSearchParams(params, { replace: true });
//   }, [searchQuery, activeTab, flatIdFromUrl, setSearchParams]);

//   // Fetch commission settings on mount
//   useEffect(() => {
//     dispatch(GetPublicSettings()).then(() => {
//       setSettingsLoaded(true);
//     });
//   }, [dispatch]);

//   // Update commission rates when settings change
//   useEffect(() => {
//     if (commissionSettings) {
//       updateCommissionRates(commissionSettings);
//     }
//   }, [commissionSettings]);

//   const customRates = commissionSettings
//     ? {
//         ADVANCE: (commissionSettings.advanceCommission ?? 0) / 100,
//         MONTHLY_RENT: (commissionSettings.rentCommission ?? 0) / 100,
//         LATE_FEE: commissionSettings.lateFee ?? 0,
//         GRACE_PERIOD: commissionSettings.gracePeriod ?? 5,
//         IS_ACTIVE: commissionSettings.isServiceActive ?? true,
//       }
//     : null;

//   const fetchRequests = useCallback(async (showTabLoader = false) => {
//     if (!tenantId) return;

//     if (showTabLoader) {
//       setTabLoading(true);
//     } else {
//       setLoading(true);
//     }

//     try {
//       const res = await dispatch(GetTenantInterestRequests({ tenantId }));
//       const allData = res?.payload?.data?.data || [];

//       if (flatIdFromUrl) {
//         const filtered = allData.filter(
//           (r) => r.flatId === flatIdFromUrl || r.flat_id === flatIdFromUrl
//         );
//         setRequests(filtered);
//       } else {
//         setRequests(allData);
//       }
//     } catch (error) {
//       console.error("Error fetching requests:", error);
//     } finally {
//       setLoading(false);
//       setTabLoading(false);
//     }
//   }, [dispatch, tenantId, flatIdFromUrl]);

//   useEffect(() => {
//     if (tenantId) {
//       fetchRequests();
//     }
//   }, [fetchRequests, tenantId]);

//   // Auto-refresh when window regains focus
//   useEffect(() => {
//     const handleFocus = () => {
//       if (tenantId) {
//         fetchRequests();
//       }
//     };

//     window.addEventListener("focus", handleFocus);
//     return () => window.removeEventListener("focus", handleFocus);
//   }, [fetchRequests, tenantId]);

//   const handleTabChange = async (event, newValue) => {
//     setTabLoading(true);
//     setActiveTab(newValue);

//     // Refresh data when tab changes
//     await fetchRequests(true);

//     setTimeout(() => {
//       setTabLoading(false);
//     }, 300);
//   };

//   const handleSearchChange = (e) => {
//     setSearchQuery(e.target.value);
//   };

//   const handleClearSearch = () => {
//     setSearchQuery("");
//   };

//   const handleOpenPaymentDialog = (request) => {
//     setPaymentDialog({ open: true, request });
//     setPaymentMethod("safepay");
//     setReceiptImage(null);
//     setReceiptPreview(null);
//     setPaymentError("");
//   };

//   const handleClosePaymentDialog = () => {
//     setPaymentDialog({ open: false, request: null });
//     setReceiptImage(null);
//     setReceiptPreview(null);
//     setPaymentError("");
//   };

//   const handleReceiptUpload = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       setReceiptImage(file);
//       setReceiptPreview(URL.createObjectURL(file));
//     }
//   };

//   const handlePayWithSafePay = async () => {
//     const { request } = paymentDialog;
//     if (!request) return;

//     setPaymentLoading(true);
//     setPaymentError("");

//     try {
//       const advanceAmount = Number(request.flatAdvance || 0);
//       const rentAmount = Number(request.flatRent || 0);
//       const advanceBreakdown = calculateAdvancePayment(rentAmount, customRates);

//       let totalAmount = advanceBreakdown.totalTenantPays + advanceAmount;
//       let safePayFee = Math.ceil(totalAmount / 0.9672) - totalAmount;
//       totalAmount += safePayFee;

//       const pendingPaymentInfo = {
//         requestId: request._id || request.id,
//         flatId: request.flatId,
//         flatName: request.flatName,
//         baseAdvance: advanceAmount,
//         advanceCommission: advanceBreakdown.commissionAmount,
//         firstMonthRent: rentAmount,
//         totalAmount: totalAmount,
//         billType: "advance",
//         tenantId: tenantId,
//         landlordId: request.landlordId,
//         paymentMethod: "safepay",
//       };
//       localStorage.setItem("pending_payment", JSON.stringify(pendingPaymentInfo));

//       const result = await dispatch(
//         CreateSafePaySession({
//           paymentData: {
//             amount: totalAmount,
//             baseAdvance: advanceAmount,
//             commissionAmount: advanceBreakdown.commissionAmount,
//             firstMonthRent: rentAmount,
//             billType: "advance",
//             billId: request._id || request.id,
//             tenantId: tenantId,
//             landlordId: request.landlordId,
//             flatId: request.flatId,
//             description: `Advance payment for ${request.flatName}`,
//             processingAmount: safePayFee,
//           },
//         })
//       );

//       const checkoutUrl =
//         result?.payload?.data?.checkoutUrl ||
//         result?.payload?.data?.data?.checkoutUrl ||
//         result?.payload?.data?.url;

//       if (checkoutUrl) {
//         window.open(checkoutUrl, "_blank");
//         handleClosePaymentDialog();
//       } else {
//         localStorage.removeItem("pending_payment");
//         setPaymentError(
//           result?.payload?.data?.message ||
//             "Failed to initiate payment. Please try again."
//         );
//       }
//     } catch (err) {
//       localStorage.removeItem("pending_payment");
//       setPaymentError("Something went wrong. Please try again.");
//     } finally {
//       setPaymentLoading(false);
//     }
//   };

//   const handleSubmitReceipt = async () => {
//     const { request } = paymentDialog;
//     if (!request || !receiptImage) {
//       setPaymentError("Please upload a receipt image");
//       return;
//     }

//     setPaymentLoading(true);
//     setPaymentError("");

//     try {
//       const formData = new FormData();
//       formData.append("image", receiptImage);
//       formData.append("requestId", request._id || request.id);
//       formData.append(
//         "amount",
//         calculateAdvancePayment(request.flatAdvance || 0, customRates)
//           .totalTenantPays + Number(request.flatRent || 0)
//       );
//       formData.append("flatId", request.flatId);
//       formData.append("flatName", request.flatName);
//       formData.append("baseAdvance", request.flatAdvance || 0);
//       formData.append(
//         "commissionAmount",
//         calculateAdvancePayment(request.flatAdvance || 0, customRates)
//           .commissionAmount
//       );
//       formData.append("firstMonthRent", request.flatRent);
//       formData.append("billType", "advance");
//       formData.append("billId", request._id || request.id);
//       formData.append("tenantId", request.tenantId);
//       formData.append("landlordId", request.landlordId);
//       formData.append("description", `Advance payment for ${request.flatName}`);

//       const uploadRes = await axios.post(
//         `${BASE_URL}/flat-interest/upload-receipt`,
//         formData,
//         {
//           headers: { "Content-Type": "multipart/form-data" },
//         }
//       );

//       if (uploadRes?.data?.success) {
//         handleClosePaymentDialog();
//         fetchRequests();
//       } else {
//         setPaymentError("Failed to upload receipt. Please try again.");
//       }
//     } catch (err) {
//       setPaymentError("Something went wrong. Please try again.");
//     } finally {
//       setPaymentLoading(false);
//     }
//   };

//   const getStatusColor = (request) => {
//     if (request.paymentStatus === "verified") return "success";
//     if (request.paymentStatus === "verifying") return "warning";
//     if (request.paymentStatus === "failed") return "error";

//     const status = request.status;
//     switch (status) {
//       case "pending":
//         return "warning";
//       case "accepted":
//         return "info";
//       case "payment_pending":
//         return "warning";
//       case "completed":
//         return "success";
//       case "rejected":
//       case "payment_rejected":
//         return "error";
//       default:
//         return "default";
//     }
//   };

//   const getStatusLabel = (request) => {
//     if (request.paymentStatus === "verified") return "Payment Verified";
//     if (request.paymentStatus === "verifying") return "Verifying Payment";
//     if (request.paymentStatus === "failed") return "Payment Failed";

//     const status = request.status;
//     switch (status) {
//       case "pending":
//         return "Pending Approval";
//       case "accepted":
//         return "Accepted - Pay Now";
//       case "payment_pending":
//         return "Verifying Payment";
//       case "completed":
//         return "Confirmed";
//       case "rejected":
//         return "Rejected";
//       case "payment_rejected":
//         return "Payment Rejected";
//       default:
//         return status;
//     }
//   };

//   const getStatusIcon = (request) => {
//     if (request.paymentStatus === "verified") return <CheckCircleIcon />;
//     if (request.paymentStatus === "verifying") return <HourglassEmptyIcon />;
//     if (request.paymentStatus === "failed") return <CancelIcon />;

//     const status = request.status;
//     switch (status) {
//       case "pending":
//         return <HourglassEmptyIcon />;
//       case "accepted":
//         return <PaymentIcon />;
//       case "payment_pending":
//         return <ReceiptIcon />;
//       case "completed":
//         return <CheckCircleIcon />;
//       case "rejected":
//       case "payment_rejected":
//         return <CancelIcon />;
//       default:
//         return null;
//     }
//   };

//   const CustomArrow = ({ onClick, direction }) => (
//     <div
//       className={`custom-arrow ${direction}`}
//       onClick={onClick}
//       role="button"
//     />
//   );

//   const sliderSettings = {
//     dots: false,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 1,
//     slidesToScroll: 1,
//     arrows: true,
//     autoplay: true,
//     autoplaySpeed: 4000,
//     nextArrow: <CustomArrow direction="next" />,
//     prevArrow: <CustomArrow direction="prev" />,
//   };

//   // Filter requests based on search and tab
//   const filterRequests = (requests) => {
//     if (!searchQuery.trim()) return requests;

//     const query = searchQuery.toLowerCase();
//     return requests.filter(request =>
//       (request.flatName?.toLowerCase().includes(query)) ||
//       (request.landlordName?.toLowerCase().includes(query)) ||
//       (request.moveInDate?.toLowerCase().includes(query)) ||
//       (request.status?.toLowerCase().includes(query))
//     );
//   };

//   const getFilteredRequests = () => {
//     let filtered = requests;

//     if (flatIdFromUrl) {
//       filtered = requests;
//     } else {
//       switch (activeTab) {
//         case 0:
//           filtered = requests;
//           break;
//         case 1:
//           filtered = requests.filter((r) => r.status === "pending");
//           break;
//         case 2:
//           filtered = requests.filter(
//             (r) => r.status === "accepted" && r.paymentStatus !== "verifying"
//           );
//           break;
//         case 3:
//           filtered = requests.filter(
//             (r) => r.status === "accepted" && r.paymentStatus === "verifying"
//           );
//           break;
//         case 4:
//           filtered = requests.filter((r) => r.status === "rejected");
//           break;
//         default:
//           filtered = requests;
//       }
//     }

//     return filterRequests(filtered);
//   };

//   const filteredRequests = getFilteredRequests();

//   // Calculate statistics
//   const stats = {
//     total: requests.length,
//     pending: requests.filter((r) => r.status === "pending").length,
//     accepted: requests.filter(
//       (r) => r.status === "accepted" && r.paymentStatus !== "verifying"
//     ).length,
//     verifying: requests.filter(
//       (r) => r.status === "accepted" && r.paymentStatus === "verifying"
//     ).length,
//     rejected: requests.filter((r) => r.status === "rejected").length,
//   };

//   // Skeleton Card Component
//   const SkeletonCard = () => (
//     <Grid item xs={12} md={6} lg={4}>
//       <Card elevation={2} sx={{ borderRadius: 3 }}>
//         <Skeleton variant="rectangular" height={200} />
//         <CardContent>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
//             <Skeleton width="60%" height={32} />
//             <Skeleton width={80} height={32} />
//           </Box>
//           <Skeleton width="100%" height={40} sx={{ mb: 2 }} />
//           <Stack spacing={1}>
//             <Skeleton width="90%" height={20} />
//             <Skeleton width="85%" height={20} />
//             <Skeleton width="80%" height={20} />
//             <Skeleton width="75%" height={20} />
//           </Stack>
//           <Skeleton width="100%" height={48} sx={{ mt: 2, borderRadius: 2 }} />
//         </CardContent>
//       </Card>
//     </Grid>
//   );

//   // Statistics Card Component
//   const StatCard = ({ title, value, icon, color, total }) => {
//     const percentage = total > 0 ? (value / total) * 100 : 0;

//     return (
//       <Card
//         elevation={2}
//         sx={{
//           p: 2.5,
//           borderRadius: 3,
//           height: '100%',
//           background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
//           border: `1px solid ${color}30`,
//           transition: 'all 0.3s ease-in-out',
//           '&:hover': {
//             transform: 'translateY(-4px)',
//             boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
//           }
//         }}
//       >
//         <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
//           <Box>
//             <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
//               {title}
//             </Typography>
//             <Typography variant="h3" fontWeight={700} color={color}>
//               {value}
//             </Typography>
//           </Box>
//           <Avatar
//             sx={{
//               bgcolor: color,
//               width: 48,
//               height: 48,
//               boxShadow: `0 4px 12px ${color}40`,
//             }}
//           >
//             {icon}
//           </Avatar>
//         </Box>
//         <Box sx={{ mt: 2 }}>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
//             <Typography variant="caption" color="text.secondary">
//               Progress
//             </Typography>
//             <Typography variant="caption" fontWeight={600} color={color}>
//               {percentage.toFixed(0)}%
//             </Typography>
//           </Box>
//           <LinearProgress
//             variant="determinate"
//             value={percentage}
//             sx={{
//               height: 6,
//               borderRadius: 3,
//               bgcolor: `${color}20`,
//               '& .MuiLinearProgress-bar': {
//                 bgcolor: color,
//                 borderRadius: 3,
//               }
//             }}
//           />
//         </Box>
//       </Card>
//     );
//   };

//   // Get no data message
//   const getNoDataMessage = () => {
//     if (flatIdFromUrl) {
//       return {
//         title: "No Payment Request Found",
//         description: "The payment link may have expired or is invalid.",
//       };
//     }

//     const messages = {
//       0: {
//         title: "No Requests Yet",
//         description: searchQuery
//           ? `No requests match your search "${searchQuery}"`
//           : "You haven't sent any property interest requests yet. Start exploring properties!",
//       },
//       1: {
//         title: "No Pending Requests",
//         description: searchQuery
//           ? `No pending requests match your search "${searchQuery}"`
//           : "You don't have any pending requests awaiting landlord approval.",
//       },
//       2: {
//         title: "No Accepted Requests",
//         description: searchQuery
//           ? `No accepted requests match your search "${searchQuery}"`
//           : "No requests have been accepted yet. Once a landlord accepts, they'll appear here.",
//       },
//       3: {
//         title: "No Payments Being Verified",
//         description: searchQuery
//           ? `No verifying payments match your search "${searchQuery}"`
//           : "You don't have any payments currently being verified by landlords.",
//       },
//       4: {
//         title: "No Rejected Requests",
//         description: searchQuery
//           ? `No rejected requests match your search "${searchQuery}"`
//           : "Great! You don't have any rejected requests.",
//       },
//     };

//     return messages[activeTab] || messages[0];
//   };

//   const noDataInfo = getNoDataMessage();

//   const renderRequestCard = (request) => (
//     <Grid item xs={12} md={6} lg={4} key={request._id || request.id}>
//       <Zoom in={true}>
//         <Card
//           elevation={3}
//           sx={{
//             height: "100%",
//             display: 'flex',
//             flexDirection: 'column',
//             transition: 'all 0.3s ease-in-out',
//             '&:hover': {
//               transform: 'translateY(-8px)',
//               boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
//             },
//             borderRadius: 3,
//             overflow: 'hidden',
//             position: 'relative',
//             '&::before': {
//               content: '""',
//               position: 'absolute',
//               top: 0,
//               left: 0,
//               right: 0,
//               height: '4px',
//               background: request.paymentStatus === "verified"
//                 ? 'linear-gradient(90deg, #4caf50 0%, #388e3c 100%)'
//                 : request.paymentStatus === "verifying"
//                 ? 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)'
//                 : request.status === "accepted"
//                 ? 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)'
//                 : request.status === "rejected"
//                 ? 'linear-gradient(90deg, #f44336 0%, #d32f2f 100%)'
//                 : 'linear-gradient(90deg, #ff9800 0%, #f57c00 100%)',
//             }
//           }}
//         >
//           {request?.flatDetail?.images?.length > 0 ? (
//             <Box className="flat-slider" sx={{ position: 'relative' }}>
//               <Slider {...sliderSettings}>
//                 {request.flatDetail.images.map((img, i) => (
//                   <Box
//                     key={i}
//                     component="img"
//                     src={img}
//                     alt={`Flat ${request.flatName} ${i}`}
//                     sx={{
//                       width: "100%",
//                       height: 220,
//                       objectFit: "cover",
//                     }}
//                   />
//                 ))}
//               </Slider>
//               <Chip
//                 icon={getStatusIcon(request)}
//                 label={getStatusLabel(request)}
//                 color={getStatusColor(request)}
//                 size="small"
//                 sx={{
//                   position: 'absolute',
//                   top: 12,
//                   right: 12,
//                   fontWeight: 600,
//                   boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
//                 }}
//               />
//             </Box>
//           ) : (
//             <Box sx={{ position: 'relative' }}>
//               <Box
//                 component="img"
//                 src={cardPic}
//                 alt="No flat image"
//                 sx={{
//                   width: "100%",
//                   height: 220,
//                   objectFit: "cover",
//                   filter: "brightness(0.9)",
//                 }}
//               />
//               <Chip
//                 icon={getStatusIcon(request)}
//                 label={getStatusLabel(request)}
//                 color={getStatusColor(request)}
//                 size="small"
//                 sx={{
//                   position: 'absolute',
//                   top: 12,
//                   right: 12,
//                   fontWeight: 600,
//                   boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
//                 }}
//               />
//             </Box>
//           )}

//           <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//             {/* Header */}
//             <Box sx={{ mb: 2 }}>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
//                 <HomeIcon color="primary" />
//                 <Typography variant="h6" fontWeight={700}>
//                   {request.flatName || "Flat"}
//                 </Typography>
//               </Box>
//               <Typography variant="h5" color="primary" fontWeight={700}>
//                 Rs {Number(request.flatRent || 0).toLocaleString()}/month
//               </Typography>
//             </Box>

//             <Divider sx={{ my: 2 }} />

//             {/* Request Details */}
//             <Stack spacing={1.5} sx={{ mb: 2 }}>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <PaymentIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>Advance:</strong> Rs{" "}
//                   {Number(request.flatAdvance || 0).toLocaleString()}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <PaymentIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>Rent:</strong> Rs{" "}
//                   {Number(request.flatRent || 0).toLocaleString()}
//                 </Typography>
//               </Box>

//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>Owner:</strong> {request.landlordName || "N/A"}
//                 </Typography>
//               </Box>

//               {request.dueDate && (
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                   <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                   <Typography variant="body2">
//                     <strong>Due Date:</strong> {request.dueDate}
//                   </Typography>
//                 </Box>
//               )}

//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>Move-in:</strong> {request.moveInDate || "N/A"}
//                 </Typography>
//               </Box>

//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <ReceiptIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>OTS Fees:</strong> Rs{" "}
//                   {calculateAdvancePayment(
//                     request.flatRent || 0,
//                     customRates
//                   ).commissionAmount.toLocaleString()}
//                 </Typography>
//               </Box>

//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
//                 <Typography variant="body2">
//                   <strong>Submitted:</strong>{" "}
//                   {moment(request.createdAt).format("DD MMM YYYY")}
//                 </Typography>
//               </Box>
//             </Stack>

//             {/* Payment Summary */}
//             <Paper
//               elevation={0}
//               sx={{
//                 bgcolor: '#f5f5f5',
//                 p: 1.5,
//                 borderRadius: 2,
//                 mb: 2,
//               }}
//             >
//               <Typography variant="caption" color="text.secondary" fontWeight={600}>
//                 Payment Summary
//               </Typography>
//               <Typography variant="h6" color="primary" fontWeight={700}>
//                 Rs{" "}
//                 {(
//                   calculateAdvancePayment(request.flatAdvance || 0, customRates)
//                     .totalTenantPays + request.flatRent
//                 ).toLocaleString()}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 + SafePay Fee (3.28% approx)
//               </Typography>
//             </Paper>

//             {/* Landlord Response */}
//             {request.responseMessage && (
//               <Paper
//                 elevation={0}
//                 sx={{
//                   bgcolor: '#fff8e1',
//                   p: 1.5,
//                   borderRadius: 2,
//                   mb: 2,
//                   borderLeft: '3px solid #ff9800'
//                 }}
//               >
//                 <Typography variant="caption" color="text.secondary" fontWeight={600}>
//                   Owner's Message:
//                 </Typography>
//                 <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
//                   "{request.responseMessage}"
//                 </Typography>
//               </Paper>
//             )}

//             {/* Escrow Flow Status */}
//             {(request.paymentStatus === "verifying" ||
//               request.paymentStatus === "verified" ||
//               request.escrowStatus === "held" ||
//               request.escrowStatus === "released") && (
//               <Paper
//                 elevation={0}
//                 sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, mb: 2 }}
//               >
//                 <Box
//                   sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
//                 >
//                   <LockIcon color="primary" fontSize="small" />
//                   <Typography variant="subtitle2" fontWeight={600}>
//                     Escrow Payment Status
//                   </Typography>
//                 </Box>
//                 <Stepper
//                   activeStep={
//                     request.escrowStatus === "released" ||
//                     request.paymentStatus === "verified"
//                       ? 3
//                       : request.escrowStatus === "held"
//                       ? 2
//                       : request.paymentStatus === "verifying"
//                       ? 1
//                       : 0
//                   }
//                   alternativeLabel
//                   sx={{ mb: 2 }}
//                 >
//                   <Step>
//                     <StepLabel>Paid</StepLabel>
//                   </Step>
//                   <Step>
//                     <StepLabel>In Escrow</StepLabel>
//                   </Step>
//                   <Step>
//                     <StepLabel>Verified</StepLabel>
//                   </Step>
//                   <Step>
//                     <StepLabel>Released</StepLabel>
//                   </Step>
//                 </Stepper>

//                 {request.escrowStatus === "held" && (
//                   <Alert severity="info" icon={<AccountBalanceWalletIcon />}>
//                     Your payment is securely held in escrow. Landlord will verify
//                     and release.
//                   </Alert>
//                 )}

//                 {(request.escrowStatus === "released" ||
//                   request.paymentStatus === "verified") && (
//                   <Alert severity="success" icon={<CheckCircleIcon />}>
//                     Payment verified & released to landlord. Your tenancy is now
//                     active!
//                   </Alert>
//                 )}

//                 {request.paymentStatus === "verifying" &&
//                   !request.escrowStatus && (
//                     <Alert severity="warning" icon={<HourglassEmptyIcon />}>
//                       Payment received. Waiting for landlord verification.
//                     </Alert>
//                   )}
//               </Paper>
//             )}

//             {/* Action Buttons */}
//             {request.status === "accepted" &&
//               (request.paymentStatus === "unpaid" ||
//                 request.paymentStatus === "failed") && (
//               <Button
//                 variant="contained"
//                 color="primary"
//                 fullWidth
//                 startIcon={
//                   settingsLoading ? (
//                     <CircularProgress size={20} color="inherit" />
//                   ) : (
//                     <PaymentIcon />
//                   )
//                 }
//                 onClick={() => handleOpenPaymentDialog(request)}
//                 disabled={!settingsLoaded || settingsLoading || requests.some((val)=> val.paymentStatus === 'verifying')}
//                 sx={{
//                   py: 1.2,
//                   fontWeight: 600,
//                   borderRadius: 2,
//                   textTransform: 'none',
//                   boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
//                   '&:hover': {
//                     boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
//                   }
//                 }}
//               >
//                 {settingsLoading ? "Loading..." : "Make Payment"}
//               </Button>
//             )}

//             {/* Status Messages */}
//             {request.status === "completed" && !request.paymentStatus && (
//               <Alert severity="success" sx={{ borderRadius: 2 }}>
//                 Booking confirmed! Contact owner for next steps.
//               </Alert>
//             )}

//             {(request.status === "rejected" ||
//               request.status === "payment_rejected") && (
//               <Alert severity="error" sx={{ borderRadius: 2 }}>
//                 {request.status === "payment_rejected"
//                   ? "Your payment was rejected. Please contact owner."
//                   : "Your request was rejected."}
//               </Alert>
//             )}
//           </CardContent>
//         </Card>
//       </Zoom>
//     </Grid>
//   );

//   return (
//     <>
//       <Wrapper />

//       <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
//         <Container maxWidth="xl" sx={{ py: 4 }}>
//           {/* Header Section */}
//           <Fade in={true} timeout={800}>
//             <Box sx={{ mb: 4 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//                 <Typography
//                   variant="h4"
//                   fontWeight={700}
//                   sx={{
//                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//                     WebkitBackgroundClip: 'text',
//                     WebkitTextFillColor: 'transparent',
//                   }}
//                 >
//                   {flatIdFromUrl ? "Payment Request" : "My Requests"}
//                 </Typography>
//                 {flatIdFromUrl && (
//                   <Button
//                     variant="outlined"
//                     size="small"
//                     onClick={() => (window.location.href = "/my-requests")}
//                     sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
//                   >
//                     View All Requests
//                   </Button>
//                 )}
//               </Box>
//               <Typography variant="body2" color="text.secondary">
//                 {flatIdFromUrl
//                   ? "Complete your payment to confirm booking."
//                   : "Track your property interest requests and make payments."}
//               </Typography>
//             </Box>
//           </Fade>

//           {/* Statistics Cards */}
//           {!flatIdFromUrl && (
//             <Fade in={!loading} timeout={1000}>
//               <Grid container spacing={3} sx={{ mb: 4 }}>
//                 <Grid item xs={12} sm={6} md={2.4}>
//                   <StatCard
//                     title="Total Requests"
//                     value={stats.total}
//                     icon={<AssignmentIcon />}
//                     color="#6366f1"
//                     total={stats.total}
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={2.4}>
//                   <StatCard
//                     title="Pending"
//                     value={stats.pending}
//                     icon={<HourglassEmptyIcon />}
//                     color="#ff9800"
//                     total={stats.total}
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={2.4}>
//                   <StatCard
//                     title="Accepted"
//                     value={stats.accepted}
//                     icon={<CheckCircleIcon />}
//                     color="#2196f3"
//                     total={stats.total}
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={2.4}>
//                   <StatCard
//                     title="Verifying"
//                     value={stats.verifying}
//                     icon={<PaymentIcon />}
//                     color="#4caf50"
//                     total={stats.total}
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={2.4}>
//                   <StatCard
//                     title="Rejected"
//                     value={stats.rejected}
//                     icon={<CancelIcon />}
//                     color="#f44336"
//                     total={stats.total}
//                   />
//                 </Grid>
//               </Grid>
//             </Fade>
//           )}

//           {/* Search Bar */}
//           {!flatIdFromUrl && (
//             <Paper
//               elevation={0}
//               sx={{
//                 mb: 2,
//                 p: 2,
//                 borderRadius: 2,
//                 bgcolor: "white",
//                 border: "1px solid #e5e7eb",
//               }}
//             >
//               <TextField
//                 fullWidth
//                 placeholder="Search by flat name, owner, move-in date, or status..."
//                 value={searchQuery}
//                 onChange={handleSearchChange}
//                 disabled={loading || tabLoading}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <SearchIcon sx={{ color: "#64748b" }} />
//                     </InputAdornment>
//                   ),
//                   endAdornment: searchQuery && (
//                     <InputAdornment position="end">
//                       <Button
//                         size="small"
//                         onClick={handleClearSearch}
//                         disabled={loading || tabLoading}
//                         sx={{
//                           minWidth: "auto",
//                           p: 0.5,
//                           color: "#64748b",
//                           "&:hover": {
//                             bgcolor: "#f1f5f9",
//                           },
//                         }}
//                       >
//                         <ClearIcon fontSize="small" />
//                       </Button>
//                     </InputAdornment>
//                   ),
//                 }}
//                 sx={{
//                   "& .MuiOutlinedInput-root": {
//                     borderRadius: 2,
//                     bgcolor: "#f8fafc",
//                     "& fieldset": {
//                       borderColor: "transparent",
//                     },
//                     "&:hover fieldset": {
//                       borderColor: "#667eea",
//                     },
//                     "&.Mui-focused fieldset": {
//                       borderColor: "#667eea",
//                     },
//                   },
//                 }}
//               />
//               {searchQuery && !loading && !tabLoading && (
//                 <Typography
//                   variant="caption"
//                   color="text.secondary"
//                   sx={{ mt: 1, display: "block" }}
//                 >
//                   Showing {filteredRequests.length} result{filteredRequests.length !== 1 ? "s" : ""} for "{searchQuery}"
//                 </Typography>
//               )}
//             </Paper>
//           )}

//           {/* Tabs Section */}
//           {!flatIdFromUrl && (
//             <Paper
//               elevation={0}
//               sx={{
//                 borderRadius: 2,
//                 overflow: 'hidden',
//                 mb: 3,
//                 border: "1px solid #e5e7eb",
//               }}
//             >
//               <Tabs
//                 value={activeTab}
//                 onChange={handleTabChange}
//                 variant="scrollable"
//                 scrollButtons="auto"
//                 sx={{
//                   bgcolor: 'background.paper',
//                   '& .MuiTab-root': {
//                     minHeight: 64,
//                     minWidth: 140,
//                     textTransform: 'none',
//                     fontWeight: 600,
//                     fontSize: '0.95rem',
//                     transition: 'all 0.3s',
//                     px: 3,
//                     color: "#64748b",
//                     '&:hover': {
//                       bgcolor: 'action.hover',
//                     },
//                     '&.Mui-selected': {
//                       color: '#667eea',
//                     },
//                     '&.Mui-disabled': {
//                       opacity: 0.6,
//                     },
//                   },
//                   '& .MuiTabs-indicator': {
//                     height: 3,
//                     borderRadius: '3px 3px 0 0',
//                     backgroundColor: '#667eea',
//                   }
//                 }}
//               >
//                 <Tab
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
//                       <AssignmentIcon fontSize="small" />
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>
//                           All
//                         </Typography>
//                         <Badge
//                           badgeContent={stats.total}
//                           color="primary"
//                           sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', mt: 0.5 } }}
//                         />
//                       </Box>
//                     </Box>
//                   }
//                   disabled={loading || tabLoading}
//                 />
//                 <Tab
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
//                       <HourglassEmptyIcon fontSize="small" />
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>
//                           Pending
//                         </Typography>
//                         <Badge
//                           badgeContent={stats.pending}
//                           color="warning"
//                           sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', mt: 0.5 } }}
//                         />
//                       </Box>
//                     </Box>
//                   }
//                   disabled={loading || tabLoading}
//                 />
//                 <Tab
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
//                       <CheckCircleIcon fontSize="small" />
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>
//                           Accepted
//                         </Typography>
//                         <Badge
//                           badgeContent={stats.accepted}
//                           color="info"
//                           sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', mt: 0.5 } }}
//                         />
//                       </Box>
//                     </Box>
//                   }
//                   disabled={loading || tabLoading}
//                 />
//                 <Tab
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
//                       <PaymentIcon fontSize="small" />
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>
//                           Verifying
//                         </Typography>
//                         <Badge
//                           badgeContent={stats.verifying}
//                           color="success"
//                           sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', mt: 0.5 } }}
//                         />
//                       </Box>
//                     </Box>
//                   }
//                   disabled={loading || tabLoading}
//                 />
//                 <Tab
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
//                       <CancelIcon fontSize="small" />
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>
//                           Rejected
//                         </Typography>
//                         <Badge
//                           badgeContent={stats.rejected}
//                           color="error"
//                           sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', mt: 0.5 } }}
//                         />
//                       </Box>
//                     </Box>
//                   }
//                   disabled={loading || tabLoading}
//                 />
//               </Tabs>
//             </Paper>
//           )}

//           {/* Requests Grid */}
//           <Box>
//             {loading || tabLoading ? (
//               <Grid container spacing={3}>
//                 {[1, 2, 3, 4, 5, 6].map((item) => (
//                   <SkeletonCard key={item} />
//                 ))}
//               </Grid>
//             ) : filteredRequests.length === 0 ? (
//               <Paper
//                 elevation={0}
//                 sx={{
//                   py: 8,
//                   borderRadius: 2,
//                   bgcolor: "white",
//                   border: "1px solid #e5e7eb",
//                 }}
//               >
//                 <Box
//                   sx={{
//                     textAlign: "center",
//                     maxWidth: 500,
//                     mx: "auto",
//                     px: 3,
//                   }}
//                 >
//                   <AssignmentIcon sx={{ fontSize: 80, color: "#cbd5e1", mb: 2 }} />
//                   <Typography
//                     variant="h5"
//                     sx={{
//                       fontWeight: 700,
//                       color: "#1e293b",
//                       mt: 3,
//                       mb: 1,
//                     }}
//                   >
//                     {noDataInfo.title}
//                   </Typography>
//                   <Typography
//                     variant="body1"
//                     color="text.secondary"
//                     sx={{ mb: 3, lineHeight: 1.6 }}
//                   >
//                     {noDataInfo.description}
//                   </Typography>
//                   {searchQuery ? (
//                     <Button
//                       variant="contained"
//                       onClick={handleClearSearch}
//                       sx={{
//                         bgcolor: "#667eea",
//                         px: 3,
//                         py: 1,
//                         borderRadius: 2,
//                         textTransform: "none",
//                         fontWeight: 600,
//                         "&:hover": {
//                           bgcolor: "#5568d3",
//                         },
//                       }}
//                     >
//                       Clear Search
//                     </Button>
//                   ) : !flatIdFromUrl && (
//                     <Button
//                       variant="contained"
//                       onClick={() => (window.location.href = "/find-property")}
//                       sx={{
//                         bgcolor: "#667eea",
//                         px: 3,
//                         py: 1,
//                         borderRadius: 2,
//                         textTransform: "none",
//                         fontWeight: 600,
//                         "&:hover": {
//                           bgcolor: "#5568d3",
//                         },
//                       }}
//                     >
//                       Find Property
//                     </Button>
//                   )}
//                 </Box>
//               </Paper>
//             ) : (
//               <Fade in={true} timeout={600}>
//                 <Grid container spacing={3}>
//                   {filteredRequests.map(renderRequestCard)}
//                 </Grid>
//               </Fade>
//             )}
//           </Box>
//         </Container>

//         {/* Payment Dialog */}
//         <Dialog
//           open={paymentDialog.open}
//           onClose={handleClosePaymentDialog}
//           maxWidth="sm"
//           fullWidth
//           PaperProps={{
//             sx: {
//               borderRadius: 3,
//             }
//           }}
//         >
//           <DialogTitle sx={{ pb: 1 }}>
//             <Typography variant="h6" fontWeight={700}>
//               Make Payment
//             </Typography>
//           </DialogTitle>
//           <DialogContent>
//             {paymentError && (
//               <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
//                 {paymentError}
//               </Alert>
//             )}

//             {/* Flat Info */}
//             <Paper
//               elevation={0}
//               sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2, mb: 3 }}
//             >
//               <Typography variant="subtitle1" fontWeight={600}>
//                 {paymentDialog.request?.flatName}
//               </Typography>
//             </Paper>

//             {/* Loading indicator */}
//             {(!settingsLoaded || settingsLoading) && (
//               <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
//                 <CircularProgress />
//               </Box>
//             )}

//             {/* Payment Breakdown */}
//             {paymentDialog.request &&
//               settingsLoaded &&
//               !settingsLoading &&
//               (() => {
//                 const advanceAmount = Number(
//                   paymentDialog.request?.flatAdvance || 0
//                 );
//                 const rentAmount = Number(paymentDialog.request?.flatRent || 0);
//                 const advanceBreakdown = calculateAdvancePayment(
//                   rentAmount,
//                   customRates
//                 );
//                 const grandTotal =
//                   advanceBreakdown.totalTenantPays + advanceAmount;

//                 return (
//                   <Box sx={{ mb: 3 }}>
//                     <PaymentBreakdown
//                       baseAmount={advanceAmount}
//                       commissionAmount={advanceBreakdown.commissionAmount}
//                       commissionPercentage={
//                         advanceBreakdown.commissionPercentage
//                       }
//                       lateFee={0}
//                       totalAmount={advanceBreakdown.totalTenantPays}
//                       landlordReceives={advanceBreakdown.landlordReceives}
//                       type="advance"
//                       isLate={false}
//                       daysOverdue={0}
//                       paymentMethod={paymentMethod}
//                     />

//                     <Paper
//                       elevation={0}
//                       sx={{
//                         border: "1px solid #e0e0e0",
//                         borderRadius: 2,
//                         p: 2,
//                         mt: 2,
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           display: "flex",
//                           justifyContent: "space-between",
//                           alignItems: "center",
//                         }}
//                       >
//                         <Typography variant="body1">
//                           First Month Rent
//                         </Typography>
//                         <Typography variant="body1" fontWeight={600}>
//                           {formatCurrency(rentAmount)}
//                         </Typography>
//                       </Box>
//                     </Paper>

//                     {paymentMethod === "safepay" && (
//                       <Paper
//                         elevation={0}
//                         sx={{
//                           border: "1px solid #e0e0e0",
//                           borderRadius: 2,
//                           p: 2,
//                           mt: 2,
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             display: "flex",
//                             justifyContent: "space-between",
//                             alignItems: "center",
//                           }}
//                         >
//                           <Typography variant="body1">
//                             SafePay Processing Fee
//                           </Typography>
//                           <Typography variant="body1" fontWeight={600}>
//                             3.28%
//                           </Typography>
//                         </Box>
//                       </Paper>
//                     )}

//                     <Paper
//                       elevation={0}
//                       sx={{
//                         bgcolor: "primary.main",
//                         color: "white",
//                         p: 2,
//                         borderRadius: 2,
//                         mt: 2,
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           display: "flex",
//                           justifyContent: "space-between",
//                           alignItems: "center",
//                         }}
//                       >
//                         <Typography variant="h6" fontWeight={600}>
//                           Grand Total
//                         </Typography>
//                         <Typography variant="h5" fontWeight={700}>
//                           {formatCurrency(
//                             paymentMethod === "safepay"
//                               ? Math.ceil(grandTotal / 0.9672)
//                               : grandTotal
//                           )}
//                         </Typography>
//                       </Box>
//                     </Paper>
//                   </Box>
//                 );
//               })()}

//             {/* Payment Method Selection */}
//             <FormControl component="fieldset" sx={{ mb: 3 }}>
//               <FormLabel component="legend">Choose Payment Method</FormLabel>
//               <RadioGroup
//                 value={paymentMethod}
//                 onChange={(e) => setPaymentMethod(e.target.value)}
//               >
//                 <FormControlLabel
//                   value="safepay"
//                   control={<Radio />}
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <CreditCardIcon color="primary" />
//                       <Box>
//                         <Typography variant="body1">
//                           Pay Online (SafePay)
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary">
//                           Secure online payment via card/bank
//                         </Typography>
//                       </Box>
//                     </Box>
//                   }
//                 />
//                 <FormControlLabel
//                   value="cash"
//                   control={<Radio />}
//                   label={
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ReceiptIcon color="success" />
//                       <Box>
//                         <Typography variant="body1">
//                           Cash / Bank Transfer
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary">
//                           Pay in cash and upload receipt
//                         </Typography>
//                       </Box>
//                     </Box>
//                   }
//                 />
//               </RadioGroup>
//             </FormControl>

//             {/* Cash Receipt Upload */}
//             {paymentMethod === "cash" && (
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" gutterBottom>
//                   Upload Payment Receipt
//                 </Typography>
//                 <Button
//                   variant="outlined"
//                   component="label"
//                   startIcon={<CloudUploadIcon />}
//                   fullWidth
//                   sx={{ mb: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
//                 >
//                   {receiptImage ? "Change Receipt" : "Upload Receipt Image"}
//                   <input
//                     type="file"
//                     hidden
//                     accept="image/*"
//                     onChange={handleReceiptUpload}
//                   />
//                 </Button>

//                 {receiptPreview && (
//                   <Box
//                     component="img"
//                     src={receiptPreview}
//                     alt="Receipt Preview"
//                     sx={{
//                       width: "100%",
//                       maxHeight: 200,
//                       objectFit: "contain",
//                       borderRadius: 2,
//                       border: "1px solid #ddd",
//                     }}
//                   />
//                 )}
//               </Box>
//             )}
//           </DialogContent>
//           <DialogActions sx={{ px: 3, pb: 3 }}>
//             <Button
//               onClick={handleClosePaymentDialog}
//               disabled={paymentLoading}
//               sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
//             >
//               Cancel
//             </Button>
//             {paymentMethod === "safepay" ? (
//               <Button
//                 variant="contained"
//                 color="primary"
//                 onClick={handlePayWithSafePay}
//                 disabled={paymentLoading || !settingsLoaded || settingsLoading}
//                 startIcon={
//                   paymentLoading ? (
//                     <CircularProgress size={20} />
//                   ) : (
//                     <CreditCardIcon />
//                   )
//                 }
//                 sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
//               >
//                 Pay Now
//               </Button>
//             ) : (
//               <Button
//                 variant="contained"
//                 color="success"
//                 onClick={handleSubmitReceipt}
//                 disabled={paymentLoading || !receiptImage}
//                 startIcon={
//                   paymentLoading ? (
//                     <CircularProgress size={20} />
//                   ) : (
//                     <CloudUploadIcon />
//                   )
//                 }
//                 sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
//               >
//                 Submit Receipt
//               </Button>
//             )}
//           </DialogActions>
//         </Dialog>
//       </div>
//     </>
//   );
// }
